import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PendingClaimsPanel } from '@/components/admin/PendingClaimsPanel';
import { adminSidebar } from '@/config/sidebarConfig';
import {
  type GetClubsParams,
  type PendingClaimItem,
  approveClaimApi,
  getClubsApi,
  getPendingClaimsApi,
  rejectClaimApi,
} from '@/lib/api/clubsApi';
import { getAdminStatsApi } from '@/lib/api/usersApi';
import { type ExportColumn, type ExportFormat, datedFilename, exportData } from '@/lib/export/export';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { updateSearchParams } from '@/lib/url-params';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getNameInitials } from '@shared';
import { ClubPlan } from '@shared/enums/domain.enum';
import type { Club } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building,
  ChevronLeft,
  ChevronRight,
  Crown,
  Download,
  ExternalLink,
  Eye,
  MapPin,
  Search,
  Users,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import ClubDetailDrawer from '../../components/admin/ClubDetailDrawer';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Helpers ────────────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'from-purple-500 to-indigo-600',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-red-600',
  'from-cyan-500 to-blue-600',
  'from-violet-500 to-purple-600',
  'from-lime-500 to-green-600',
];

function clubGradient(id: number): string {
  return AVATAR_GRADIENTS[id % AVATAR_GRADIENTS.length] ?? AVATAR_GRADIENTS[0];
}

async function fetchAllForExport(params: GetClubsParams): Promise<Club[]> {
  const limit = 100;
  let p = 1;
  const all: Club[] = [];
  while (true) {
    const res = await getClubsApi({ ...params, limit, page: p });
    all.push(...res.data);
    if (p >= res.meta.totalPages) break;
    p++;
  }
  return all;
}

const CLUB_EXPORT_COLUMNS: ExportColumn<Club>[] = [
  { header: 'ID', value: 'id' },
  { header: 'Nom', value: 'name' },
  { header: 'Alias', value: 'alias' },
  { header: 'Ville', value: c => c.city ?? '' },
  { header: 'Département', value: c => c.department ?? '' },
  { header: 'Région', value: c => c.region ?? '' },
  { header: 'Plan', value: 'clubPlan' },
  { header: 'Actif', value: c => (c.isActive ? 'Oui' : 'Non') },
  { header: 'Inscrit le', value: c => c.createdAt.slice(0, 10) },
];

// ─── Types ───────────────────────────────────────────────────────────────────

type TabKey = 'all' | 'active' | 'inactive' | 'pro';
type SortKey = 'date_desc' | 'name_asc' | 'name_desc';

const VALID_TABS = new Set<string>(['all', 'active', 'inactive', 'pro']);
const VALID_SORTS = new Set<string>(['date_desc', 'name_asc', 'name_desc']);

const ITEMS_PER_PAGE = 25;

// ─── Sort key → API params mapping ───────────────────────────────────────────

function sortKeyToParams(sortKey: SortKey): { sortBy: string; sortOrder: 'asc' | 'desc' } {
  if (sortKey === 'name_asc') return { sortBy: 'name', sortOrder: 'asc' };
  if (sortKey === 'name_desc') return { sortBy: 'name', sortOrder: 'desc' };
  return { sortBy: 'createdAt', sortOrder: 'desc' };
}

// ─── Tab → API params mapping ────────────────────────────────────────────────

function tabToParams(tab: TabKey): Pick<GetClubsParams, 'isActive' | 'clubPlan'> {
  if (tab === 'active') return { isActive: true };
  if (tab === 'inactive') return { isActive: false };
  if (tab === 'pro') return { clubPlan: ClubPlan.PRO };
  return {};
}

// ─── URL param helpers ────────────────────────────────────────────────────────

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminClubs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();

  // ─── Read filters from URL ───
  const urlTab = searchParams.get('tab') ?? 'all';
  const urlSort = searchParams.get('sort') ?? 'date_desc';
  const urlPage = Math.max(1, Number(searchParams.get('page') ?? '1'));

  const tab = (VALID_TABS.has(urlTab) ? urlTab : 'all') as TabKey;
  const sortKey = (VALID_SORTS.has(urlSort) ? urlSort : 'date_desc') as SortKey;
  const page = urlPage;

  // ─── searchInput is local (debounce buffer); search is URL-persisted ───
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const search = searchParams.get('search') ?? '';

  const [exportLoading, setExportLoading] = useState(false);
  const [detailClubId, setDetailClubId] = useState<number | null>(null);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(prev => updateSearchParams(prev, updates), { replace: true });
  }

  // Build query params from current filter state
  const filterParams: GetClubsParams = {
    page,
    limit: ITEMS_PER_PAGE,
    search: search || undefined,
    ...sortKeyToParams(sortKey),
    ...tabToParams(tab),
  };

  const {
    data: clubsResponse,
    isLoading,
    isError: clubsError,
    refetch: refetchClubs,
  } = useQuery({
    queryKey: queryKeys.clubs.list(filterParams as Record<string, unknown>),
    queryFn: () => getClubsApi(filterParams),
    staleTime: 2 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  });

  const { data: platformStats } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: getAdminStatsApi,
    staleTime: 5 * 60 * 1000,
  });

  const { data: pendingClaims, isLoading: claimsLoading } = useQuery({
    queryKey: queryKeys.clubs.pendingClaims(),
    queryFn: getPendingClaimsApi,
    staleTime: 60 * 1000,
    enabled: isAuthenticated,
  });

  const claims = useMemo<PendingClaimItem[]>(() => pendingClaims ?? [], [pendingClaims]);

  const approveMutation = useMutation({
    mutationFn: (claimId: number) => approveClaimApi(claimId),
    onSuccess: () => {
      toast.success('Demande approuvée avec succès');
      void queryClient.invalidateQueries({ queryKey: queryKeys.clubs.pendingClaims() });
    },
    onError: err => {
      toast.error(err instanceof Error ? err.message : "Erreur lors de l'approbation");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ claimId, reason }: { claimId: number; reason?: string }) => rejectClaimApi(claimId, reason),
    onSuccess: () => {
      toast.success('Demande rejetée');
      void queryClient.invalidateQueries({ queryKey: queryKeys.clubs.pendingClaims() });
    },
    onError: err => {
      toast.error(err instanceof Error ? err.message : 'Erreur lors du rejet');
    },
  });

  function handleSearch(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ search: value.trim() || undefined, page: undefined });
    }, 350);
  }

  function handleTab(t: TabKey) {
    updateParams({ tab: t !== 'all' ? t : undefined, page: undefined });
  }

  function handleSort(s: SortKey) {
    updateParams({ sort: s !== 'date_desc' ? s : undefined, page: undefined });
  }

  function handleClearFilters() {
    setSearchInput('');
    updateParams({ search: undefined, tab: undefined, sort: undefined, page: undefined });
  }

  async function handleExport(format: ExportFormat = 'csv') {
    setExportLoading(true);
    try {
      const exportParams: GetClubsParams = {
        search: search || undefined,
        ...sortKeyToParams(sortKey),
        ...tabToParams(tab),
      };
      const all = await fetchAllForExport(exportParams);
      await exportData(all, CLUB_EXPORT_COLUMNS, datedFilename('clubs', format), format, { sheetName: 'Clubs' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors de l'export");
    } finally {
      setExportLoading(false);
    }
  }

  // Pagination from API meta
  const meta = clubsResponse?.meta;
  const paginated = useMemo<Club[]>(() => clubsResponse?.data ?? [], [clubsResponse]);
  const totalPages = meta?.totalPages ?? 1;
  const totalCount = meta?.total ?? 0;
  const pageNumbers = getPageNumbers(page, totalPages);
  const hasFilters = search !== '' || tab !== 'all' || sortKey !== 'date_desc';

  const clubStats = platformStats?.clubs;

  // Tab counts from platformStats
  const counts = useMemo(
    () => ({
      all: clubStats?.total ?? 0,
      active: clubStats?.active ?? 0,
      inactive: clubStats ? clubStats.total - clubStats.active : 0,
      pro: clubStats?.byPlan.PRO ?? 0,
    }),
    [clubStats],
  );

  const frontUrl =
    (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5232';

  const statCards = [
    {
      icon: Building,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      label: 'Total clubs',
      value: String(clubStats?.total ?? 0),
      trend: clubStats?.newThisMonth ? `+${clubStats.newThisMonth} ce mois` : undefined,
    },
    {
      icon: Users,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      label: 'Clubs actifs',
      value: String(clubStats?.active ?? 0),
      subtitle: `${counts.inactive} inactif${counts.inactive > 1 ? 's' : ''}`,
    },
    {
      icon: Crown,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      label: 'Plan Pro',
      value: String(counts.pro),
      subtitle: `Gratuit : ${clubStats?.byPlan.FREE ?? 0}`,
    },
    {
      icon: XCircle,
      iconBg: 'bg-slate-50',
      iconColor: 'text-slate-400',
      label: 'Clubs inactifs',
      value: String(counts.inactive),
      subtitle: 'Désactivés manuellement',
    },
  ];

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'all', label: 'Tous' },
    { key: 'active', label: 'Actifs' },
    { key: 'inactive', label: 'Inactifs' },
    { key: 'pro', label: 'Plan Pro' },
  ];

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8'>
        <PageHeader
          title='Gestion des Clubs'
          subtitle='Administration des clubs inscrits sur la plateforme'
          actions={
            <div className='flex items-center gap-2'>
              <button
                onClick={() => void handleExport('csv')}
                disabled={exportLoading}
                className='flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition text-sm font-medium text-slate-600 disabled:opacity-50'
                title='Exporter CSV'
              >
                <Download className='w-4 h-4' />
                {exportLoading ? 'Export…' : 'CSV'}
              </button>
              <button
                onClick={() => void handleExport('xlsx')}
                disabled={exportLoading}
                className='flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition text-sm font-medium text-slate-600 disabled:opacity-50'
                title='Exporter Excel'
              >
                <Download className='w-4 h-4' />
                {exportLoading ? 'Export…' : 'Excel'}
              </button>
            </div>
          }
        />

        {/* KPI Cards */}
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          {statCards.map(s => (
            <StatCard
              key={s.label}
              icon={s.icon}
              iconBg={s.iconBg}
              iconColor={s.iconColor}
              label={s.label}
              value={s.value}
              trend={s.trend}
              subtitle={s.subtitle}
            />
          ))}
        </div>

        {/* ── Pending Club Admin Claims ───────────────────────────────── */}
        <PendingClaimsPanel
          claims={claims}
          isLoading={claimsLoading || !isAuthenticated}
          onApprove={claimId => approveMutation.mutate(claimId)}
          onReject={(claimId, reason) => rejectMutation.mutate({ claimId, reason })}
          approvePending={approveMutation.isPending}
          rejectPending={rejectMutation.isPending}
        />

        {/* Filters */}
        <div className='bg-white rounded-xl border border-slate-200 p-4 mb-4'>
          <div className='flex flex-wrap items-center gap-3'>
            <div className='relative flex-1 min-w-[200px] max-w-sm'>
              <Search className='w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2' />
              <input
                type='text'
                value={searchInput}
                onChange={e => handleSearch(e.target.value)}
                placeholder='Rechercher par nom, alias, ville, région…'
                className='w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none'
              />
            </div>

            <select
              value={sortKey}
              onChange={e => handleSort(e.target.value as SortKey)}
              className='px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none'
            >
              <option value='date_desc'>Plus récents</option>
              <option value='name_asc'>Nom A → Z</option>
              <option value='name_desc'>Nom Z → A</option>
            </select>

            {hasFilters && (
              <button
                onClick={handleClearFilters}
                className='px-3 py-2 text-sm text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 rounded-lg transition'
              >
                Effacer les filtres
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className='flex gap-1 mb-4 border-b border-slate-200'>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => handleTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition flex items-center gap-2 ${
                tab === t.key
                  ? 'bg-white border border-b-white border-slate-200 text-blue-600 -mb-px'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
              <span
                className={`text-xs rounded-full px-2 py-0.5 ${
                  tab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>

        {clubsError && <QueryErrorState onRetry={() => void refetchClubs()} />}

        {/* Table */}
        <div className='bg-white rounded-xl border border-slate-200'>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='bg-slate-50 border-b border-slate-200'>
                <tr>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Club</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Localisation</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Plan</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Statut</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase'>Inscrit le</th>
                  <th className='px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase'>Actions</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {isLoading || !isAuthenticated ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className='animate-pulse'>
                      <td className='px-5 py-4'>
                        <div className='flex items-center gap-3'>
                          <div className='w-9 h-9 rounded-full bg-slate-100' />
                          <div className='space-y-1'>
                            <div className='h-3.5 w-36 bg-slate-100 rounded-full' />
                            <div className='h-3 w-20 bg-slate-100 rounded-full' />
                          </div>
                        </div>
                      </td>
                      {Array.from({ length: 4 }).map((__, j) => (
                        <td key={j} className='px-5 py-4'>
                          <div className='h-5 w-20 bg-slate-100 rounded-full' />
                        </td>
                      ))}
                      <td className='px-5 py-4' />
                    </tr>
                  ))
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='px-5 py-16 text-center text-slate-400'>
                      Aucun club trouvé.
                    </td>
                  </tr>
                ) : (
                  paginated.map(club => (
                    <tr
                      key={club.id}
                      className={`hover:bg-slate-50 transition-colors ${!club.isActive ? 'opacity-60' : ''}`}
                    >
                      {/* Club */}
                      <td className='px-5 py-3.5'>
                        <div className='flex items-center gap-3'>
                          <div
                            className={`w-9 h-9 flex-shrink-0 rounded-full bg-gradient-to-br ${clubGradient(club.id)} flex items-center justify-center text-white text-xs font-bold`}
                          >
                            {getNameInitials(club.name)}
                          </div>
                          <div className='min-w-0'>
                            <p className='font-semibold text-slate-900 truncate max-w-[200px]'>{club.name}</p>
                            <p className='text-xs text-slate-400 font-mono'>#{club.alias}</p>
                          </div>
                        </div>
                      </td>

                      {/* Localisation */}
                      <td className='px-5 py-3.5'>
                        {club.city ? (
                          <div className='flex items-center gap-1.5 text-slate-600'>
                            <MapPin className='w-3.5 h-3.5 flex-shrink-0 text-slate-400' />
                            <span className='text-sm'>
                              {club.city}
                              {club.department && <span className='text-slate-400'> ({club.department})</span>}
                            </span>
                          </div>
                        ) : (
                          <span className='text-slate-300 text-xs'>—</span>
                        )}
                      </td>

                      {/* Plan */}
                      <td className='px-5 py-3.5'>
                        {club.clubPlan === ClubPlan.PRO ? (
                          <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700'>
                            <Crown className='w-3 h-3' />
                            Pro
                          </span>
                        ) : (
                          <span className='inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500'>
                            Gratuit
                          </span>
                        )}
                      </td>

                      {/* Statut */}
                      <td className='px-5 py-3.5'>
                        {club.isActive ? (
                          <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700'>
                            <span className='w-1.5 h-1.5 rounded-full bg-emerald-500' />
                            Actif
                          </span>
                        ) : (
                          <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500'>
                            <span className='w-1.5 h-1.5 rounded-full bg-slate-400' />
                            Inactif
                          </span>
                        )}
                      </td>

                      {/* Inscrit le */}
                      <td className='px-5 py-3.5 text-slate-500 text-xs whitespace-nowrap'>
                        {formatDateAbbreviatedFr(club.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className='px-5 py-3.5'>
                        <div className='flex items-center justify-end gap-1'>
                          <button
                            onClick={() => setDetailClubId(club.id)}
                            className='p-2 hover:bg-slate-100 rounded-lg transition text-slate-400 hover:text-purple-600'
                            title='Gérer / détail'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                          <a
                            href={`${frontUrl}/clubs/${club.id}`}
                            target='_blank'
                            rel='noreferrer'
                            className='p-2 hover:bg-slate-100 rounded-lg transition text-slate-400 hover:text-blue-600'
                            title='Voir le profil public'
                          >
                            <ExternalLink className='w-4 h-4' />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && isAuthenticated && totalCount > 0 && (
            <div className='px-5 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3'>
              <p className='text-sm text-slate-500'>
                {totalCount} club{totalCount > 1 ? 's' : ''}
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                    disabled={page === 1}
                    className='px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-40'
                  >
                    <ChevronLeft className='w-4 h-4' />
                  </button>
                  {pageNumbers.map((n, i) =>
                    n === '...' ? (
                      <span key={`ellipsis-${i}`} className='px-3 py-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => updateParams({ page: String(n) })}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                          page === n ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => updateParams({ page: String(Math.min(totalPages, page + 1)) })}
                    disabled={page === totalPages}
                    className='px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-40'
                  >
                    <ChevronRight className='w-4 h-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ClubDetailDrawer clubId={detailClubId} onClose={() => setDetailClubId(null)} frontUrl={frontUrl} />
    </DashboardLayout>
  );
}
